import React, { useState, useEffect, useRef } from 'react';
import { formatMoney } from '../utils/formatMoney';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from './config/api';
import { useOrders } from '../hooks/useOrders';
import { useSocket } from '../hooks/useSocket';
import useOrderAcceptance from '../hooks/useOrderAcceptance';
import ManualOrderAcceptanceModal from './ManualOrderAcceptanceModal';
import OrderPreviewModal from './OrderPreviewModal';
import DispatchModal from './DispatchModal';
import CountdownTimer from './CountdownTimer';
import AutoAcceptControlModal from './AutoAcceptControlModal';
import BoxRejectModal from './BoxRejectModal';
import InlineOrderAcceptance from './InlineOrderAcceptance';
import IncomingCallPopup from './IncomingCallPopup';
import CancelOrderModal from './CancelOrderModal';
import { useToast } from './shared/Toast';
import OrderPreloadBanner from './OrderPreloadBanner';
import OrderDriver from './delivery/OrderDriver';
import { storeAPI } from '../services/api';
import { extractCustomerInfo } from '../utils/orderHelpers';
import { parseTimestamp } from '../utils/timeUtils';
// 24ωρη ώρα χωρίς «24:15» (το hour12:false με el-GR το έδινε μετά τα μεσάνυχτα)· η ζώνη μένει του browser όπως πριν.
import { fmtClock } from '../utils/athensDate';
import { cancellationPrintWarning } from '../utils/cancellationPrint';
import { useTrainingMode } from '../hooks/useTrainingMode';
import TrainingCompleteButton from './shared/TrainingCompleteButton';
import { 
  Search, X, Eye, EyeOff, RefreshCw, ChevronDown, ChevronRight,
  Clock, MapPin, Phone, User, Settings2, Package
} from 'lucide-react';

// Import logos
import efoodLogo from '../assets/logos/logo_efood.png';
import boxLogo from '../assets/logos/logo_box.png';
import woltLogo from '../assets/logos/logo_wolt.png';
import storeLogo from '../assets/logos/logo.png';

// Store delivery settings for cross-platform delivery
interface StoreDeliverySettings {
  [platform: string]: {
    is_self_delivery: boolean;
    has_efood_lastmile: boolean;
    has_wolt_drive: boolean;
  };
}

const platformLogos: Record<string, string> = {
  efood: efoodLogo,
  box: boxLogo,
  wolt: woltLogo,
  internal: storeLogo,
};

const OrdersPage: React.FC = () => {
  const { store, platforms, updatePlatforms, employee } = useAuth();
  const trainingModeEnabled = useTrainingMode(store?.id);
  const { showToast } = useToast();
  const empPerms = (employee?.permissions || {}) as Record<string, boolean>;
  // Cancel permission mirrors the backend gate in routes/orders.js:
  //  - admins (store_admin/super_admin/all) → any order
  //  - cancel_all_orders → any order
  //  - cancel_orders → only platform-settled orders (delivery by efood/wolt/box,
  //    money handled by the platform, not the cashier)
  const isAdminRole = employee?.role === 'store_admin' || employee?.role === 'super_admin';
  const isPlatformSettled = (o: any) => {
    const plat = String(o?.platform_name || '').toLowerCase();
    const dm = String(o?.delivery_method || '').toLowerCase();
    return ['efood', 'wolt', 'box'].includes(plat) && ['efood', 'wolt', 'box'].includes(dm);
  };
  const canCancelOrder = (o: any): boolean => {
    if (!o) return false;
    if (['cancelled', 'rejected'].includes(o.status)) return false;
    // Admins / full-freedom cancel anything, any status. cancel_orders (cashier)
    // cancels platform-settled orders — including completed ones.
    if (isAdminRole || empPerms.all === true || empPerms.cancel_all_orders === true) return true;
    if (empPerms.cancel_orders === true) return isPlatformSettled(o);
    return false;
  };
  const [cancelTarget, setCancelTarget] = useState<any | null>(null);
  const { orders, loading, refreshOrders } = useOrders();
  const { t } = useTranslation(['orders', 'common']);
  // «Νέα εμφάνιση»: κεφαλίδα όπως στις άλλες οθόνες· τα φίλτρα/κάρτες τα ντύνει το CashierV2*.css.
  const v2 = useTheme().look === 'v2';
  const { socket } = useSocket();
  const { acceptOrderById } = useOrderAcceptance();
  
  // Store delivery settings for each platform
  const [storeDeliverySettings, setStoreDeliverySettings] = useState<StoreDeliverySettings>({});
  
  // Search & filter
  const [searchQuery, setSearchQuery] = useState('');
  const [showCompletedOrders, setShowCompletedOrders] = useState(false);
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [orderTypeFilter, setOrderTypeFilter] = useState<string>('all');
  
  // Expanded row
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 20;
  
  // Track seen order IDs for notification sound
  const seenOrderIds = useRef<Set<number>>(new Set());
  
  // Modal states
  const [showAutoAcceptModal, setShowAutoAcceptModal] = useState(false);
  const [showManualAcceptanceModal, setShowManualAcceptanceModal] = useState(false);
  const [pendingOrder, setPendingOrder] = useState<any>(null);
  const [previewOrder, setPreviewOrder] = useState<any>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [dispatchOrder, setDispatchOrder] = useState<any>(null);
  const [showBoxRejectModal, setShowBoxRejectModal] = useState(false);
  const [boxRejectOrder, setBoxRejectOrder] = useState<any>(null);

  // Fetch store delivery settings
  useEffect(() => {
    const fetchDeliverySettings = async () => {
      if (!store?.id) return;
      try {
        const flagsResponse = await fetch(`${API_BASE_URL}/api/stores/${store.id}/platform-flags`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
        });
        if (flagsResponse.ok) {
          const flags = await flagsResponse.json();
          const settings: StoreDeliverySettings = {};
          if (platforms) {
            platforms.forEach((p: any) => {
              settings[p.platform_name] = {
                is_self_delivery: p.is_self_delivery || false,
                has_efood_lastmile: flags.has_efood_lastmile || false,
                has_wolt_drive: flags.has_wolt_drive || false
              };
            });
          }
          setStoreDeliverySettings(settings);
        }
      } catch (error) {
        console.error('Error fetching delivery settings:', error);
      }
    };
    fetchDeliverySettings();
  }, [store?.id, platforms]);

  // Sort orders: pending first, then accepted, then dispatched, then terminal statuses
  const statusPriority: Record<string, number> = { pending: 0, pending_accept: 0, accepted: 1, ready: 2, dispatched: 3, delivered: 4, completed: 5, rejected: 6, cancelled: 7 };
  
  // No hard client cap here — the backend already scopes to the current register
  // period (see useOrders). Pagination below limits what's actually rendered, so
  // every completed order since the last close is reachable.
  const allOrders = [...orders].sort((a, b) => {
    if (showCompletedOrders) {
      // When showing all orders, sort by timestamp only so completed orders aren't buried
      return new Date(b.order_timestamp).getTime() - new Date(a.order_timestamp).getTime();
    }
    const pa = statusPriority[a.status] ?? 4;
    const pb = statusPriority[b.status] ?? 4;
    if (pa !== pb) return pa - pb;
    return new Date(b.order_timestamp).getTime() - new Date(a.order_timestamp).getTime();
  });
  
  // Filter orders
  const filteredOrders = allOrders.filter((order: any) => {
    if (!showCompletedOrders && ['completed', 'delivered', 'rejected', 'cancelled'].includes(order.status)) return false;
    if (platformFilter !== 'all' && order.platform_name !== platformFilter) return false;
    if (orderTypeFilter !== 'all' && order.order_type !== orderTypeFilter) return false;
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    const info = extractCustomerInfo(order);
    const orderNumber = (order.short_code || order.platform_order_id || '').toString().toLowerCase();
    
    return (
      info.name.toLowerCase().includes(query) ||
      info.summary.toLowerCase().includes(query) ||
      info.phone.toLowerCase().includes(query) ||
      orderNumber.includes(query)
    );
  });
  
  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const paginatedOrders = filteredOrders.slice((currentPage - 1) * ordersPerPage, currentPage * ordersPerPage);

  // Reset page on filter change
  useEffect(() => { setCurrentPage(1); }, [orders.length, searchQuery, platformFilter, orderTypeFilter, showCompletedOrders]);

  // New order sound notification
  useEffect(() => {
    const pendingPlatformOrders = orders.filter(o => o.status === 'pending' && o.platform_name !== 'internal');
    const newOrders = pendingPlatformOrders.filter(order => !seenOrderIds.current.has(order.id));
    if (newOrders.length > 0 && seenOrderIds.current.size > 0) {
      const audio = new Audio('/notification.mp3');
      audio.play().catch(() => {});
    }
    orders.forEach(order => { seenOrderIds.current.add(order.id); });
  }, [orders]);

  // WebSocket listeners
  useEffect(() => {
    if (!socket) return;
    const handleOrderUpdate = () => { refreshOrders(); };
    socket.on('order-updated', handleOrderUpdate);
    return () => { socket.off('order-updated', handleOrderUpdate); };
  }, [socket, refreshOrders]);

  // Auto-accept helpers
  const getAutoAcceptStatus = () => {
    const hasAutoAccept = platforms?.some(p => p.auto_accept_orders) || false;
    const avgDeliveryTime = platforms?.length > 0 
      ? Math.round(platforms.reduce((sum, p) => sum + (p.est_delivery_time || 30), 0) / platforms.length) : 30;
    return { isAutoAcceptEnabled: hasAutoAccept, currentDeliveryTime: avgDeliveryTime };
  };

  const handleAutoAcceptUpdate = async (enabled: boolean, deliveryTime: number, platform?: 'efood' | 'box' | 'wolt' | 'all') => {
    if (!store?.id || !platforms) return;
    try {
      if (platform === 'all' || !platform) {
        await Promise.all(platforms.map(p => storeAPI.updatePlatform(store.id, p.platform_name, { auto_accept_orders: enabled, est_delivery_time: deliveryTime })));
      } else {
        await storeAPI.updatePlatform(store.id, platform, { auto_accept_orders: enabled, est_delivery_time: deliveryTime });
      }
      // Update auth context so the UI reflects the change without a refresh
      updatePlatforms(platform ?? 'all', { auto_accept_orders: enabled, est_delivery_time: deliveryTime });
    } catch (error) {
      console.error('Failed to update auto-accept settings:', error);
      throw error;
    }
  };

  // Manual acceptance/rejection handlers
  const handleManualOrderAccept = async (deliveryTime: number) => {
    if (!pendingOrder) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${store?.id}/orders/${pendingOrder.id}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` },
        body: JSON.stringify({ delivery_time: deliveryTime })
      });
      if (!response.ok) throw new Error(t('common:errors.acceptOrder'));
      setShowManualAcceptanceModal(false);
      setPendingOrder(null);
      refreshOrders();
    } catch (error) {
      console.error('Error accepting order:', error);
      throw error;
    }
  };

  const handleManualOrderReject = async (reason?: string) => {
    if (!pendingOrder) return;
    // BOX and efood need a reason from a fixed list — open the reason picker.
    if ((pendingOrder.platform_name === 'box' || pendingOrder.platform_name === 'efood') && !reason) {
      setBoxRejectOrder(pendingOrder);
      setShowBoxRejectModal(true);
      setShowManualAcceptanceModal(false);
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${pendingOrder.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` },
        body: JSON.stringify({ reason: reason || 'high_load' })
      });
      if (!response.ok) throw new Error(t('common:errors.rejectOrder'));
      // Box may have already auto-accepted it (its own integrator handling) —
      // the order was synced to accepted instead of rejected; tell the cashier.
      const data = await response.json().catch(() => ({} as any));
      if (data.already_accepted) showToast('info', data.message);
      setShowManualAcceptanceModal(false);
      setShowBoxRejectModal(false);
      setPendingOrder(null);
      setBoxRejectOrder(null);
      refreshOrders();
    } catch (error) {
      console.error('Error rejecting order:', error);
    }
  };

  const handleBoxOrderReject = async (reason: string) => {
    if (!boxRejectOrder) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${boxRejectOrder.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` },
        body: JSON.stringify({ reason })
      });
      if (!response.ok) throw new Error(t('common:errors.rejectOrder'));
      const data = await response.json().catch(() => ({} as any));
      if (data.already_accepted) showToast('info', data.message);
      setShowBoxRejectModal(false);
      setBoxRejectOrder(null);
      refreshOrders();
    } catch (error) {
      console.error('Error rejecting BOX order:', error);
    }
  };

  // Self-delivery check
  const checkIfSelfDelivery = (orderId: number): boolean => {
    const order = orders.find((o: any) => o.id === orderId);
    if (!order) return false;
    if (order.platform_name === 'internal' && order.order_type === 'delivery') return true;
    const platform = platforms?.find(p => p.platform_name === order.platform_name);
    return !!platform?.is_self_delivery;
  };

  // Complete order
  const completeOrderById = async (orderId: number, fiscalData?: { 
    documentType: 'receipt' | 'invoice'; 
    isTraining?: boolean;
    invoiceCustomer?: { vat_number: string; name: string; country_code: string; branch_code: number; address?: { street?: string; number?: string; postal_code?: string; city?: string; }; }
  }) => {
    if (!store?.id) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${orderId}/complete`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` },
        body: JSON.stringify({ fiscalDocumentType: fiscalData?.documentType || 'receipt', invoiceCustomer: fiscalData?.invoiceCustomer, isTraining: fiscalData?.isTraining === true })
      });
      if (!response.ok) {
        const error = await response.json();
        console.error('Failed to complete order:', error);
        showToast('error', `Η ολοκλήρωση απέτυχε: ${error.message || 'άγνωστο σφάλμα'}`);
        throw new Error(error.message || t('common:errors.completeOrder'));
      }
      const result = await response.json();
      if (result.order?.is_training) showToast('success', t('platformTraining.completed'));
      if (result.fiscal_error) {
        showToast('error', 'Η παραγγελία ολοκληρώθηκε αλλά η απόδειξη ΔΕΝ εκδόθηκε — θα επιχειρηθεί ξανά αυτόματα.');
      }
      // Η πλατφόρμα δεν δέχτηκε το «έτοιμη» (efood 422) αλλά η παραγγελία ολοκληρώθηκε.
      if (result.driver_warning) showToast('warning', result.driver_warning);
      // Browser receipt popup is a fallback only — skip it when a thermal print
      // job was queued (printJobCreated).
      if (!result.order?.is_training && result.fiscalDocument && result.fiscalDocument.success && !result.fiscalDocument.printJobCreated) {
        const order = orders.find((o: any) => o.id === orderId);
        openReceiptWindow(order, result.fiscalDocument, fiscalData?.documentType || 'receipt');
      }
      refreshOrders();
    } catch (error) {
      console.error('Error completing order:', error);
    }
  };

  // ... rest of the code remains the same ...
  // Open receipt window after completing order
  const openReceiptWindow = (order: any, fiscalData: any, documentType: string) => {
    const receiptWindow = window.open('', '_blank', 'width=400,height=600');
    if (!receiptWindow) {
      console.warn('⚠️ Popup blocked - cannot open receipt window');
      return;
    }

    const total = parseFloat(order?.total_amount || '0');
    const vatAmount = total - (total / 1.24);

    receiptWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${documentType === 'invoice' ? t('fiscal.invoice') : t('fiscal.receipt')}</title>
        <style>
          body { font-family: 'Courier New', monospace; padding: 20px; max-width: 300px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px; }
          .store-name { font-size: 18px; font-weight: bold; }
          .doc-type { font-size: 14px; font-weight: bold; margin: 10px 0; background: #f0f0f0; padding: 5px; text-align: center; }
          .item { display: flex; justify-content: space-between; margin: 5px 0; font-size: 12px; }
          .total { border-top: 1px dashed #000; margin-top: 10px; padding-top: 10px; font-weight: bold; }
          .footer { text-align: center; margin-top: 20px; font-size: 12px; border-top: 1px dashed #000; padding-top: 10px; }
          .mark { font-size: 10px; word-break: break-all; margin: 5px 0; }
          .success { color: green; font-weight: bold; }
          @media print { button { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="store-name">${store?.name || 'XENTRIX FLOW'}</div>
          <div>${new Date().toLocaleString('el-GR')}</div>
        </div>
        
        <div class="doc-type">${documentType === 'invoice' ? t('fiscal.invoiceHeader') : t('fiscal.receiptHeader')} #${fiscalData.series || '1'}-${fiscalData.documentNumber || ''}</div>
        
        <div class="total">
          <div class="item">
            <span>${t('common:total').toUpperCase()}</span>
            <span>€${total.toFixed(2)}</span>
          </div>
          <div class="item" style="font-size: 12px; font-weight: normal;">
            <span>${t('common:vat')}</span>
            <span>€${vatAmount.toFixed(2)}</span>
          </div>
        </div>
        
        <div class="footer">
          <div>${t('fiscal.platform')} ${order?.platform_name?.toUpperCase() || 'N/A'}</div>
          <div>${t('paymentMethod')} ${t('fiscal.paymentCredit')}</div>
          ${fiscalData.mark ? `<div class="mark">MARK: ${fiscalData.mark}</div>` : ''}
          ${fiscalData.uid ? `<div class="mark">UID: ${fiscalData.uid}</div>` : ''}
          <div class="success">✓ ${t('fiscal.registeredMydata')}</div>
          <div style="margin-top: 10px;">${t('fiscal.thankYou')}</div>
        </div>
        
        <button onclick="window.print()" style="width: 100%; margin-top: 20px; padding: 10px; cursor: pointer;">
          ${t('common:print')}
        </button>
      </body>
      </html>
    `);
    receiptWindow.document.close();
  };

  // Cancel an order via the soft-cancel endpoint. Reason is audit-only. When the
  // order has an active receipt, the backend cancels that receipt automatically
  // once `force` is confirmed (an AADE cancellation document is issued).
  const cancelOrder = async (orderId: number, reason: string, force?: boolean) => {
    if (!store?.id) throw new Error('Missing store context');
    const response = await fetch(`${API_BASE_URL}/api/orders/${store.id}/orders/${orderId}/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
      },
      body: JSON.stringify({ reason, force: force === true }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      // Attach the response body to the thrown error so the modal can detect
      // the "requires_force" warning and prompt for a second confirmation
      // (cancel order — the linked receipt is cancelled along with it).
      const e: any = new Error(err.message || t('common:errors.cancelOrder'));
      e.code = err.error;
      e.requiresForce = err.requires_force === true;
      e.fiscalCanCancel = err.fiscal_can_cancel === true;
      throw e;
    }
    // The order is already cancelled at this point. If it had a receipt that
    // could not be voided (a real error — not the expected no_mark case),
    // surface it so staff can void it manually from the Fiscal Documents tab.
    try {
      const body = await response.json();
      const fc = body?.fiscal_cancel;
      if (fc && fc.done !== true && fc.error !== 'no_mark') {
        showToast('error', `Η παραγγελία ακυρώθηκε, αλλά το ακυρωτικό παραστατικό ΔΕΝ εκδόθηκε (${fc.error || 'σφάλμα'}). Ακύρωσέ το από τα Παραστατικά.`);
      }
      const printWarning = fc?.done === true ? cancellationPrintWarning(fc, t) : null;
      if (printWarning) showToast('warning', printWarning);
    } catch { /* best-effort body parse */ }
    refreshOrders();
  };

  // Handle complete button click - complete directly for platform orders
  const handleCompleteClick = async (orderId: number, isTraining = false) => {
    const isSelfDelivery = checkIfSelfDelivery(orderId);
    const order = orders.find((o: any) => o.id === orderId);
    
    if (!order) return;
    
    if (isTraining || (order.is_training && ['efood', 'wolt', 'box'].includes(order.platform_name))) {
      await completeOrderById(orderId, { documentType: 'receipt', isTraining: true });
    } else if (isSelfDelivery && order.status !== 'dispatched') {
      // Αποστολή μόνο για όσες δεν έφυγαν. Σε διανομή το «Παραδόθηκε» = ολοκλήρωση (PUT /complete, όπως
      // η «Ολοκλήρωση» του παραθύρου)· άλλος διανομέας = «Αλλαγή διανομέα» (/reassign), ποτέ ξανά /assign.
      setDispatchOrder(order);
      setShowDispatchModal(true);
    } else {
      // Complete directly for all other orders (platform and internal)
      // Fiscal document type can be changed via preview modal before completing
      await completeOrderById(orderId);
    }
  };

  // Handle order dispatch
  const handleOrderDispatched = () => {
    // Refresh orders after dispatch
    refreshOrders();
    setDispatchOrder(null);
  };


  // Count orders by status
  const pendingCount = orders.filter(o => o.status === 'pending').length;
  const acceptedCount = orders.filter(o => o.status === 'accepted').length;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-surface-1 flex items-center justify-center">
        <div className="bg-surface-2 rounded-2xl shadow-xl p-8 flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-line-strong border-t-blue-500"></div>
          <p className="text-t2 font-medium">{t('loading')}</p>
        </div>
      </div>
    );
  }

  // Available platforms for filter
  const availablePlatforms = Array.from(new Set(orders.map(o => o.platform_name)));

  return (
    <div className="cashier-orders min-h-screen bg-surface-1">
      {/* Incoming Call Popup */}
      <IncomingCallPopup />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-6">

        {/* ─── Page Header ─── */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            {!v2 && <Package className="h-6 w-6 text-blue-600 dark:text-blue-400" />}
            <h1 className={v2 ? 'text-2xl font-bold text-t1 leading-tight mr-1' : 'text-xl font-bold text-t1'}>{t('title')}</h1>
            {pendingCount > 0 && (
              <span data-status="pending" className="cashier-order-count flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500 text-white animate-pulse">
                {pendingCount} {t('pending')}
              </span>
            )}
            {acceptedCount > 0 && (
              <span data-status="accepted" className="cashier-order-count flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-500 text-white">
                {acceptedCount} {t('active')}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Auto-Accept Settings */}
            <button
              onClick={() => setShowAutoAcceptModal(true)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                getAutoAcceptStatus().isAutoAcceptEnabled
                  ? 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 border border-green-300 dark:border-green-700'
                  : 'bg-surface-2 text-t3 border border-line-strong hover:border-line-strong'
              }`}
              title={t('settings:autoAccept', { ns: 'settings' })}
            >
              <Settings2 className="h-4 w-4" />
              <span className="hidden sm:inline">
                {getAutoAcceptStatus().isAutoAcceptEnabled
                  ? t('autoAcceptOn', { time: getAutoAcceptStatus().currentDeliveryTime })
                  : t('autoAcceptOff')}
              </span>
            </button>

            {/* Refresh */}
            <button
              onClick={() => refreshOrders()}
              className="p-2 rounded-lg bg-surface-2 border border-line-strong hover:bg-surface-3 active:scale-95 transition-all"
              title={t('common:refresh')}
            >
              <RefreshCw className="h-4 w-4 text-t2" />
            </button>
          </div>
        </div>

        {/* ─── Toolbar ─── */}
        <div className="cashier-order-filters bg-surface-2 rounded-xl border border-line-strong p-3 mb-4">
          <div className="flex flex-wrap items-center gap-2">
            {/* Platform Filter Pills */}
            <div className="flex items-center gap-1">
              <button
                aria-pressed={platformFilter === 'all'}
                onClick={() => setPlatformFilter('all')}
                className={`cashier-choice px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  platformFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-surface-3 text-t2 hover:bg-surface-4'
                }`}
              >
                {t('filterAll')}
              </button>
              {availablePlatforms.map(platform => (
                <button
                  key={platform}
                  aria-pressed={platformFilter === platform}
                  onClick={() => setPlatformFilter(platform)}
                  className={`cashier-choice flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    platformFilter === platform
                      ? 'bg-blue-600 text-white'
                      : 'bg-surface-3 text-t2 hover:bg-surface-4'
                  }`}
                >
                  <img src={platformLogos[platform] || efoodLogo} alt={platform} className="w-4 h-4 object-contain" />
                  {{ internal: t('common:platform.internal'), efood: 'e-food', box: 'Box', wolt: 'Wolt' }[platform] || platform}
                </button>
              ))}
            </div>

            {/* Separator */}
            <div className="w-px h-6 bg-surface-3" />

            {/* Order Type Filter Pills */}
            <div className="flex items-center gap-1">
              <button
                aria-pressed={orderTypeFilter === 'all'}
                onClick={() => setOrderTypeFilter('all')}
                className={`cashier-choice px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  orderTypeFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-surface-3 text-t2 hover:bg-surface-4'
                }`}
              >
                {t('filterAllTypes')}
              </button>
              <button
                aria-pressed={orderTypeFilter === 'takeaway'}
                onClick={() => setOrderTypeFilter('takeaway')}
                className={`cashier-choice px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  orderTypeFilter === 'takeaway'
                    ? 'bg-purple-600 text-white'
                    : 'bg-surface-3 text-t2 hover:bg-surface-4'
                }`}
              >
                Take Away
              </button>
              <button
                aria-pressed={orderTypeFilter === 'delivery'}
                onClick={() => setOrderTypeFilter('delivery')}
                className={`cashier-choice px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  orderTypeFilter === 'delivery'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-surface-3 text-t2 hover:bg-surface-4'
                }`}
              >
                Delivery
              </button>
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Toggle Completed */}
            <button
              aria-pressed={showCompletedOrders}
              onClick={() => setShowCompletedOrders(!showCompletedOrders)}
              className={`cashier-choice flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                showCompletedOrders
                  ? 'bg-surface-4 text-t1'
                  : 'bg-surface-3 text-t3 hover:text-t2'
              }`}
            >
              {showCompletedOrders ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              {showCompletedOrders ? t('hideCompleted') : t('showCompleted')}
            </button>

            {/* Search */}
            <div className="relative min-w-[180px] max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-t3" />
              <input
                type="text"
                placeholder={t('common:searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-8 py-1.5 bg-surface-3 border border-line-strong rounded-lg text-xs text-t1 placeholder-t3 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                  <X className="h-3.5 w-3.5 text-t3 hover:text-t1" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ─── Orders List ─── */}
        {paginatedOrders.length === 0 ? (
          <div className="bg-surface-2 rounded-xl border border-line-strong p-12 text-center">
            <Package className="mx-auto h-12 w-12 text-t4 mb-3" />
            <h3 className="text-sm font-medium text-t2">
              {searchQuery ? t('noOrders') : t('emptyOrders')}
            </h3>
            <p className="text-xs text-t4 mt-1">
              {searchQuery
                ? t('noMatch', { query: searchQuery })
                : t('emptyHint')}
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="mt-3 px-4 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
              >
                {t('clearSearch')}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {paginatedOrders.map((order: any) => {
              const info = extractCustomerInfo(order);
              const orderTotal = Number(order.total_amount || 0);
              const orderTime = new Date(order.order_timestamp);
              // A platform order still waiting for manual accept/reject. These
              // auto-expand (so the accept/reject controls are visible without a
              // tap) and get a prominent highlight.
              const needsAcceptance = order.status === 'pending'
                && order.platform_name !== 'internal'
                && !getAutoAcceptStatus().isAutoAcceptEnabled;
              const isExpanded = expandedOrderId === order.id || needsAcceptance;
              // Σε διανομή: «Διανομέας: …» + «Αλλαγή διανομέα» (χωρίς δικό μας διανομέα το OrderDriver δεν δείχνει τίποτα).
              // Όχι έλεγχος order_type: στις πλατφόρμες ποικίλλει (π.χ. Wolt «preorder»).
              const showDriver = order.status === 'dispatched' && !!store?.id;
              // Κάρτα σε διανομή με προφόρτωση σε εξέλιξη/αποτυχία: την κλείνει ο poller στην έγκριση ή το banner
              // (νέα δοκιμή/μετρητά) — όχι το «Παραδόθηκε», που θα την έβγαζε από τη λίστα πριν πληρωθεί.
              const awaitingPos = order.status === 'dispatched' && ['processing', 'failed'].includes(order.delivery_payment_status);

              // Status colors
              const statusConfig: Record<string, { bg: string; text: string; border: string; label: string }> = {
                pending: { bg: 'bg-amber-500', text: 'text-t1', border: 'border-l-amber-500', label: t('status.pending') },
                // Auto-accept failed; the retry scheduler re-tries within ~2'.
                // Without this entry it fell back to «Ολοκληρωμένη» — the
                // cashier saw a stuck order labelled as done.
                pending_accept: { bg: 'bg-orange-600', text: 'text-t1', border: 'border-l-orange-600', label: 'Αναμονή αποδοχής' },
                accepted: { bg: 'bg-blue-500', text: 'text-t1', border: 'border-l-blue-500', label: t('status.accepted') },
                ready: { bg: 'bg-teal-500', text: 'text-t1', border: 'border-l-teal-500', label: t('status.ready') },
                dispatched: { bg: 'bg-indigo-500', text: 'text-t1', border: 'border-l-indigo-500', label: t('status.dispatched') },
                delivered: { bg: 'bg-green-600', text: 'text-t1', border: 'border-l-green-600', label: t('status.delivered') },
                completed: { bg: 'bg-surface-4', text: 'text-t1', border: 'border-l-slate-500', label: t('status.completed') },
                rejected: { bg: 'bg-red-500', text: 'text-t1', border: 'border-l-red-500', label: t('status.rejected') },
                cancelled: { bg: 'bg-red-700', text: 'text-t1', border: 'border-l-red-700', label: t('status.cancelled') },
              };
              const sc = statusConfig[order.status] || statusConfig.completed;

              // Override label for internal takeaway completed
              const statusLabel = order.platform_name === 'internal' && order.order_type === 'takeaway' && order.status === 'completed'
                ? t('status.ready') : sc.label;

              return (
                <div key={order.id} data-status={order.status} data-needs-acceptance={needsAcceptance} className={`cashier-order-card bg-surface-2 rounded-lg border border-line-strong border-l-4 ${sc.border} overflow-hidden transition-all ${needsAcceptance ? 'ring-2 ring-amber-400/70 shadow-lg shadow-amber-500/10' : ''}`}>

                  {/* ── POS Preload Banner — only for delivery card orders that
                       are mid-preload or had it fail. Self-dismisses on success. */}
                  {(order.delivery_payment_status === 'processing' || order.delivery_payment_status === 'failed') && (
                    <div className="px-4 pt-3">
                      <OrderPreloadBanner
                        storeId={store?.id || 0}
                        orderId={order.id}
                        initialDeliveryPaymentStatus={order.delivery_payment_status}
                        onChanged={refreshOrders}
                      />
                    </div>
                  )}

                  {/* ── Pre-order Banner ── */}
                  {info.isPreOrder && info.preOrderTime && (
                    <div className="bg-red-600 text-white px-4 py-2 flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 font-bold">
                        <Clock className="h-4 w-4" />
                        ΠΡΟΠΑΡΑΓΓΕΛΙΑ
                      </div>
                      <span className="font-medium">
                        {info.preOrderTime.toLocaleDateString('el-GR', { day: '2-digit', month: 'short' })} στις{' '}
                        {fmtClock(info.preOrderTime, { zone: 'local' })}
                      </span>
                    </div>
                  )}

                  {/* ── Awaiting-acceptance banner ── */}
                  {needsAcceptance && (
                    <div className="bg-amber-500 text-slate-900 px-4 py-2 flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide animate-pulse">
                      <Clock className="h-4 w-4" />
                      {t('awaitingAcceptance')}
                    </div>
                  )}

                  {/* ── Main Row (always visible) ── */}
                  <div
                    className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-surface-3 dark:hover:bg-surface-3/50 transition-colors"
                    onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                  >
                    {/* Expand chevron */}
                    <div className="text-t4">
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </div>

                    {/* Platform logo */}
                    <img
                      src={platformLogos[order.platform_name] || efoodLogo}
                      alt={order.platform_name}
                      className="w-7 h-7 object-contain flex-shrink-0"
                    />

                    {/* Order # & Time */}
                    <div className="min-w-[100px]">
                      <div className="font-bold text-t1 text-sm">
                        #{order.short_code || order.platform_order_id?.slice(-6)}
                      </div>
                      <div className="text-xs text-t3">
                        {fmtClock(orderTime, { zone: 'local' })}
                      </div>
                    </div>

                    {/* Status Badge */}
                    <span className={`cashier-order-status inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold uppercase ${sc.bg} ${sc.text} min-w-[75px] justify-center`}>
                      {statusLabel}
                    </span>

                    {/* Customer Name */}
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      <span className="font-medium text-t1 text-sm truncate" title={info.summary || undefined}>{info.summary || info.name}</span>
                      {order.is_training && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-500/15 text-orange-700 dark:text-orange-300 flex-shrink-0">
                          {t('platformTraining.badge')}
                        </span>
                      )}
                      {info.isTakeaway && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 flex-shrink-0">
                          TAKE AWAY
                        </span>
                      )}
                    </div>

                    {/* Countdown Timer (for accepted orders) */}
                    {order.status === 'accepted' && order.accepted_at && (
                      <div className="flex-shrink-0" onClick={e => e.stopPropagation()}>
                        <CountdownTimer
                          orderTime={order.accepted_at}
                          estimatedDeliveryMinutes={order.estimated_delivery_time || 30}
                        />
                      </div>
                    )}

                    {/* Total */}
                    <div className="text-right min-w-[80px] flex-shrink-0">
                      <div className="font-bold text-t1 text-sm">{formatMoney(orderTotal)}</div>
                      <div className="text-[11px] text-t3">
                        {order.payment_type === 'cash' ? t('common:payment.cash') : order.payment_type === 'credit' ? t('common:payment.credit') : ['card', 'pos'].includes(order.payment_type) ? t('common:payment.card') : ['online', 'prepaid', 'card_online', 'credit_card'].includes(order.payment_type) ? t('common:payment.online') : (order.payment_type || t('common:payment.cash'))}
                      </div>
                    </div>

                    {/* Quick Actions (stop propagation so clicks don't toggle expand) */}
                    <div className="flex items-center gap-1.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => { setPreviewOrder(order); setShowPreviewModal(true); }}
                        className="px-2.5 py-1.5 text-xs font-medium text-t2 bg-surface-3 border border-line-strong rounded-lg hover:bg-surface-4 transition-all"
                      >
                        {t('common:view')}
                      </button>
                      {(['accepted', 'ready', 'dispatched'].includes(order.status) || (order.platform_name === 'internal' && order.status === 'pending')) && !awaitingPos && (
                        <TrainingCompleteButton
                          trainingEnabled={trainingModeEnabled && ['efood', 'wolt', 'box'].includes(order.platform_name) && ['pending', 'accepted', 'ready'].includes(order.status)}
                          isTraining={order.is_training === true && ['efood', 'wolt', 'box'].includes(order.platform_name)}
                          onComplete={training => handleCompleteClick(order.id, training)}
                          className="px-2.5 py-1.5 text-xs font-semibold rounded-lg"
                        >
                          {order.status === 'dispatched' ? t('markDelivered') : t('complete')}
                        </TrainingCompleteButton>
                      )}
                    </div>
                  </div>

                  {/* ── Expanded Details ── */}
                  {isExpanded && (
                    <div className="cashier-order-details border-t border-line-strong bg-slate-750 px-4 py-4">
                      {showDriver && (
                        <div className="-mx-4 -mt-4 mb-4">
                          <OrderDriver storeId={store!.id} orderId={order.id} shortCode={order.short_code} />
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Left: Customer Details */}
                        <div className="space-y-2.5">
                          <h4 className="text-xs font-semibold text-t3 uppercase tracking-wider">{t('common:customer')}</h4>
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-2 text-sm">
                              <User className="h-3.5 w-3.5 text-t4" />
                              <span className="text-t1 font-medium">{info.name}</span>
                            </div>
                            {info.phone && (
                              <div className="flex items-center gap-2 text-sm">
                                <Phone className="h-3.5 w-3.5 text-t4" />
                                <span className="text-t2">{info.phone}</span>
                              </div>
                            )}
                            {!info.isTakeaway && info.address && (
                              <div className="flex items-start gap-2 text-sm">
                                <MapPin className="h-3.5 w-3.5 text-t4 mt-0.5" />
                                <div>
                                  <span className="text-t2">{info.address}</span>
                                  {info.floor && <span className="text-t4 ml-2">{t('floor', { floor: info.floor })}</span>}
                                  {info.doorbell && <span className="text-t4 ml-2">{t('doorbell', { doorbell: info.doorbell })}</span>}
                                </div>
                              </div>
                            )}
                            {info.notes && (
                              <div className="flex items-start gap-2 text-sm">
                                <span className="text-t4 text-xs">{t('notesLabel')}</span>
                                <span className="text-t2">{info.notes}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Right: Order Details */}
                        <div className="space-y-2.5">
                          <h4 className="text-xs font-semibold text-t3 uppercase tracking-wider">{t('common:details')}</h4>
                          <div className="space-y-1.5 text-sm">
                            {order.estimated_delivery_time && (
                              <div className="flex justify-between">
                                <span className="text-t3">{info.isTakeaway ? t('estimatedPickup') : t('estimatedDelivery')}</span>
                                <span className="text-blue-600 dark:text-blue-400 font-medium">{t('minutesShort', { count: order.estimated_delivery_time })}</span>
                              </div>
                            )}
                            {order.status === 'accepted' && order.accepted_at && (
                              <div className="flex justify-between">
                                <span className="text-t3">{t('acceptanceTime')}</span>
                                <span className="text-green-600 dark:text-green-400 font-medium">
                                  {fmtClock(parseTimestamp(order.accepted_at), { zone: 'local' })}
                                </span>
                              </div>
                            )}
                            {order.delivery_method && (
                              <div className="flex justify-between">
                                <span className="text-t3">{t('deliveryMethod')}</span>
                                <span className={`font-medium ${
                                  order.delivery_method === 'self' ? 'text-t2' :
                                  order.delivery_method === 'efood' || order.delivery_method === 'efood_lastmile' ? 'text-red-600 dark:text-red-400' :
                                  order.delivery_method === 'wolt' || order.delivery_method === 'wolt_drive' ? 'text-cyan-600 dark:text-cyan-400' :
                                  'text-t2'
                                }`}>
                                  {order.delivery_method === 'self' && t('deliverySelf')}
                                  {order.delivery_method === 'efood' && 'e-food'}
                                  {order.delivery_method === 'wolt' && 'Wolt'}
                                  {order.delivery_method === 'efood_lastmile' && t('deliveryEfoodPending')}
                                  {order.delivery_method === 'wolt_drive' && t('deliveryWoltPending')}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-t3">{t('paymentMethod')}</span>
                              <span className="text-t2 font-medium">{order.payment_type === 'cash' ? t('common:payment.cash') : order.payment_type === 'credit' ? t('common:payment.credit') : ['card', 'pos'].includes(order.payment_type) ? t('common:payment.card') : ['online', 'prepaid', 'card_online', 'credit_card'].includes(order.payment_type) ? t('common:payment.online') : (order.payment_type || t('common:payment.cash'))}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-t3">{t('totalLabel')}</span>
                              <span className="text-t1 font-bold">{formatMoney(orderTotal)}</span>
                            </div>
                            {order.tip_amount > 0 && (
                              <div className="flex justify-between">
                                <span className="text-t3">{t('tipLabel')}</span>
                                <span className="text-green-600 dark:text-green-400 font-medium">{formatMoney(Number(order.tip_amount))}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Rejected Order Notice */}
                      {order.status === 'rejected' && (
                        <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700/50 rounded-lg flex items-center gap-2">
                          <X className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-red-700 dark:text-red-300">{t('rejected')}</p>
                            <p className="text-xs text-red-600 dark:text-red-400">
                              {t('rejectedMessage')}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Inline Acceptance Controls (for pending platform orders when auto-accept is off) */}
                      {order.status === 'pending' && order.platform_name !== 'internal' && !getAutoAcceptStatus().isAutoAcceptEnabled && (() => {
                        const platformData = platforms?.find(p => p.platform_name === order.platform_name);
                        const deliverySettings = storeDeliverySettings[order.platform_name];
                        return (
                          <div className="mt-3">
                            <InlineOrderAcceptance
                              order={order}
                              presetTimes={platformData?.preset_times}
                              storeDeliverySettings={deliverySettings}
                              onAccept={async (deliveryTime, deliveryMethod) => {
                                try {
                                  const response = await fetch(`${API_BASE_URL}/api/orders/${order.id}/accept`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
                                    },
                                    body: JSON.stringify({ estimatedTime: deliveryTime, deliveryMethod })
                                  });
                                  if (!response.ok) {
                                    const errorData = await response.json().catch(() => ({}));
                                    const err: any = new Error(errorData.message || t('common:errors.acceptOrder'));
                                    err.code = errorData.code;
                                    err.status = response.status;
                                    throw err;
                                  }
                                  refreshOrders();
                                } catch (error: any) {
                                  console.error('Error accepting order:', error);
                                  throw error;
                                }
                              }}
                              onReject={async (reason?: string) => {
                                try {
                                  const response = await fetch(`${API_BASE_URL}/api/orders/${order.id}/reject`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
                                    },
                                    body: JSON.stringify({ reason: reason || 'Rejected by store' })
                                  });
                                  if (!response.ok) {
                                    const errorData = await response.json();
                                    throw new Error(errorData.message || t('common:errors.rejectOrder'));
                                  }
                                  // Box may have already auto-accepted it — synced to
                                  // accepted instead of rejected; surface why.
                                  const data = await response.json().catch(() => ({} as any));
                                  if (data.already_accepted) showToast('info', data.message);
                                  refreshOrders();
                                } catch (error: any) {
                                  // Το μήνυμα το δείχνει το InlineOrderAcceptance, μαζί με
                                  // «Αφαίρεση από τη λίστα» — εδώ μόνο το περνάμε.
                                  console.error('Error rejecting order:', error);
                                  throw error;
                                }
                              }}
                              // Η πλατφόρμα αρνείται και απόρριψη (π.χ. efood 422 σε ακυρωμένη/
                              // ληγμένη παραγγελία): χωρίς αυτό, η σελίδα έδειχνε μόνο
                              // «status code 422» και η παραγγελία έμενε κολλημένη.
                              onDismiss={async (reason: string) => {
                                const response = await fetch(`${API_BASE_URL}/api/orders/${order.store_id}/orders/${order.id}/dismiss`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
                                  },
                                  body: JSON.stringify({ reason })
                                });
                                if (!response.ok) {
                                  const errorData = await response.json().catch(() => ({}));
                                  throw new Error(errorData.message || 'Η αφαίρεση απέτυχε.');
                                }
                                refreshOrders();
                              }}
                              onStale={refreshOrders}
                              isTakeaway={info.isTakeaway}
                              isPreOrder={info.isPreOrder}
                              preOrderTime={info.preOrderTime}
                            />
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ─── Pagination ─── */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 px-2">
            <span className="text-xs text-t3">
              {((currentPage - 1) * ordersPerPage) + 1}–{Math.min(currentPage * ordersPerPage, filteredOrders.length)} of {filteredOrders.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
                  currentPage === 1 ? 'text-t4 cursor-not-allowed' : 'text-t2 bg-surface-2 hover:bg-surface-3 border border-line-strong'
                }`}
              >
                {t('common:previous')}
              </button>
              {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 7) {
                  pageNum = i + 1;
                } else if (currentPage <= 4) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 3) {
                  pageNum = totalPages - 6 + i;
                } else {
                  pageNum = currentPage - 3 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-7 h-7 rounded text-xs font-medium transition-all ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'text-t2 bg-surface-2 hover:bg-surface-3 border border-line-strong'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
                  currentPage === totalPages ? 'text-t4 cursor-not-allowed' : 'text-t2 bg-surface-2 hover:bg-surface-3 border border-line-strong'
                }`}
              >
                {t('common:next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ─── Modals ─── */}
      <AutoAcceptControlModal
        isOpen={showAutoAcceptModal}
        onClose={() => setShowAutoAcceptModal(false)}
        currentPlatform="all"
        isAutoAcceptEnabled={getAutoAcceptStatus().isAutoAcceptEnabled}
        currentDeliveryTime={getAutoAcceptStatus().currentDeliveryTime}
        onUpdateSettings={handleAutoAcceptUpdate}
        platformSettings={platforms ? {
          efood: {
            enabled: platforms.find(p => p.platform_name === 'efood')?.auto_accept_orders || false,
            deliveryTime: platforms.find(p => p.platform_name === 'efood')?.est_delivery_time || 30
          },
          box: {
            enabled: platforms.find(p => p.platform_name === 'box')?.auto_accept_orders || false,
            deliveryTime: platforms.find(p => p.platform_name === 'box')?.est_delivery_time || 30
          },
          wolt: {
            enabled: platforms.find(p => p.platform_name === 'wolt')?.auto_accept_orders || false,
            deliveryTime: platforms.find(p => p.platform_name === 'wolt')?.est_delivery_time || 30
          }
        } : undefined}
      />

      <ManualOrderAcceptanceModal
        isOpen={showManualAcceptanceModal}
        order={pendingOrder}
        onAccept={handleManualOrderAccept}
        onReject={handleManualOrderReject}
        onClose={() => { setShowManualAcceptanceModal(false); setPendingOrder(null); }}
      />

      <OrderPreviewModal
        isOpen={showPreviewModal}
        onClose={() => { setShowPreviewModal(false); setPreviewOrder(null); refreshOrders(); }}
        order={previewOrder}
        storeId={store?.id || 0}
        showCompleteButton={['pending', 'accepted', 'ready', 'dispatched'].includes(previewOrder?.status)}
        showCancelButton={canCancelOrder(previewOrder)}
        onCancel={(o) => { setShowPreviewModal(false); setPreviewOrder(null); setCancelTarget(o); }}
      />

      <DispatchModal
        isOpen={showDispatchModal}
        onClose={() => { setShowDispatchModal(false); setDispatchOrder(null); }}
        order={dispatchOrder}
        onOrderDispatched={handleOrderDispatched}
      />

      <CancelOrderModal
        isOpen={!!cancelTarget}
        orderCode={cancelTarget?.short_code || cancelTarget?.platform_order_id}
        platformName={cancelTarget?.platform_name}
        onClose={() => setCancelTarget(null)}
        onConfirm={async (reason, force) => {
          if (!cancelTarget) return;
          await cancelOrder(cancelTarget.id, reason, force);
        }}
      />

      <BoxRejectModal
        isOpen={showBoxRejectModal}
        onClose={() => { setShowBoxRejectModal(false); setBoxRejectOrder(null); }}
        onReject={handleBoxOrderReject}
        orderNumber={boxRejectOrder?.short_code || boxRejectOrder?.platform_order_id || ''}
      />
    </div>
  );
};

export default OrdersPage;
